/**
 * Wire DTOs for the binding-management API surface.
 * `/api/bindings` GET/POST, `/api/bindings/[bindingId]` GET/DELETE,
 * `/api/bindings/cleanup-orphaned` POST.
 */

import type { Channel } from "./team.js";

export type BindingStatus = "active" | "orphaned";

export const BINDING_STATUSES: readonly BindingStatus[] = ["active", "orphaned"] as const;

export function isBindingStatus(v: unknown): v is BindingStatus {
  return v === "active" || v === "orphaned";
}

export interface Binding {
  channel: Channel;
  bindingId: string;
  agentName: string;
  /** Key Vault URI for the secret blob (versioned). NEVER the token itself. */
  botTokenSecretRef: string;
  botUsername?: string;
  status: BindingStatus;
  /** ISO 8601 */
  createdAt: string;
  /** Admin UPN who created the binding */
  createdBy: string;
}

export interface ListBindingsResponse {
  bindings: Binding[];
}

export interface CreateBindingRequest {
  channel: Channel;
  bindingId: string;
  agentName: string;
  /** Plaintext bot token — sent on the wire (HTTPS), never persisted in the row. */
  botToken: string;
  botUsername?: string;
}

export interface CreateBindingResponse {
  binding: Binding;
  /** Constructed from GATEWAY_PUBLIC_URL + channel + bindingId. */
  webhookUrl: string;
}

export interface GetBindingResponse {
  binding: Binding;
}

export interface DeleteBindingResponse {
  deleted: {
    bindingId: string;
    channel: Channel;
    conversationsCleared: number;
  };
}

export interface CleanupOrphanedResponse {
  cleaned: { bindingId: string; channel: Channel }[];
  failed: { bindingId: string; channel: Channel; originalMessage: string }[];
}

export type CascadeStep = "unregisterWebhook" | "kvSecret" | "conversations" | "bindingsRow";

/**
 * Carried in UpstreamError.details.partial when DELETE cascade fails mid-flow.
 * The CLI's `bind remove` command renders this inline for a structured retry hint.
 */
export interface CascadeFailureDetails {
  bindingId: string;
  channel: Channel;
  completedSteps: CascadeStep[];
  failedStep: CascadeStep;
  originalMessage: string;
}

// ───────────────────────────────────────────────────────────────────────────
// KV secret JSON payload — discriminated + versioned union.
//
// Each channel that uses a per-binding secret bundle defines its own variant.
// The `channel` field is the discriminator; `version` enables forward-compat
// schema migration if a variant ever needs to change shape.
//
// F4 only invokes the Telegram variant (gated by the adapter registry — POST
// /api/bindings rejects channels without a registered adapter). The Slack +
// Teams variants are defined now so F8/F10 just register their adapter
// without re-touching the union shape.
// ───────────────────────────────────────────────────────────────────────────

export interface TelegramSecretPayload {
  version: 1;
  channel: "telegram";
  botToken: string;
  /** 64 hex chars; set on Telegram's `setWebhook` via the `secret_token` param. */
  webhookSecret: string;
}

export interface SlackSecretPayload {
  version: 1;
  channel: "slack";
  /** `xoxb-...` bot user OAuth token */
  botToken: string;
  /** App-level signing secret used to verify Slack request HMACs */
  signingSecret: string;
  /** `xapp-...` app-level token, optional, for future Socket Mode */
  appLevelToken?: string;
}

export interface TeamsSecretPayload {
  version: 1;
  channel: "teams";
  appId: string;
  appPassword: string;
}

export type BindingSecretPayload =
  | TelegramSecretPayload
  | SlackSecretPayload
  | TeamsSecretPayload;

/**
 * Runtime guard for BindingSecretPayload. Used by `lib/gateway/secrets.ts`
 * to validate JSON.parse output before treating it as a typed payload.
 *
 * Strict on `version: 1` and `channel` discriminator; rejects unknown channel
 * variants so a future schema rename can't silently misroute. Field validation
 * within each variant is structural (key presence + string type) only — the
 * caller decides what to do with malformed contents.
 */
export function isBindingSecretPayload(v: unknown): v is BindingSecretPayload {
  if (v === null || typeof v !== "object") return false;
  const obj = v as Record<string, unknown>;
  if (obj.version !== 1) return false;
  if (typeof obj.channel !== "string") return false;

  if (obj.channel === "telegram") {
    return typeof obj.botToken === "string" && typeof obj.webhookSecret === "string";
  }
  if (obj.channel === "slack") {
    return (
      typeof obj.botToken === "string" &&
      typeof obj.signingSecret === "string" &&
      (obj.appLevelToken === undefined || typeof obj.appLevelToken === "string")
    );
  }
  if (obj.channel === "teams") {
    return typeof obj.appId === "string" && typeof obj.appPassword === "string";
  }
  return false;
}
